import * as React from "react";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import { FormGroup } from "components/bootstrap";
import { InputDescription, Select, useChipInput } from "components/common";
import commonStyles from "components/event-definitions/common/commonStyles.css";
import useLicenseCheck from "license/useLicenseCheck";
import useMitreNameIndex, {
  formatMitreCategory
} from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreNameIndex";
import useMitreFlatOptions from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreFlatOptions";
const MAX_TACTICS_TECHNIQUES = 64;
const VALID_PATTERN = /^(TA\d{4}|T\d{4}(\.\d{3})?)$/;
const quote = (s) => `"${s}"`;
const buildInvalidMessage = (invalidList) => {
  if (invalidList.length === 0) return null;
  return i18n.t("tactics_techniques_editor.invalid_id", {
    ns: "enterprise_security_app",
    count: invalidList.length,
    ids: invalidList.map(quote).join(", ")
  });
};
const normalizeId = (raw) => raw.trim().toUpperCase();
const validateId = (id) => VALID_PATTERN.test(id) ? null : buildInvalidMessage([id]);
const matchesCatalogue = (options, raw) => {
  const query = raw.trim().toLowerCase();
  return query.length > 0 && options.some((option) => option.label.toLowerCase().includes(query));
};
const OptionRow = styled.div(
  ({ $depth }) => css`
    display: flex;
    align-items: baseline;
    padding-left: ${$depth * 16}px;
  `
);
const OptionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ChipLabel = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;
const Editor = ({ value, onChange, disabled = false, error = null }) => {
  const { t } = useTranslation("enterprise_security_app");
  const { nameById } = useMitreNameIndex();
  const { options } = useMitreFlatOptions();
  const hasCatalogue = options.length > 0;
  const { input, messages, isAtMax, setDuplicateAttempt, flagDuplicateAttempt, selectProps, reactSelectProps } = useChipInput({
    values: value,
    onChange,
    normalize: normalizeId,
    validate: validateId,
    max: MAX_TACTICS_TECHNIQUES,
    describeInvalid: buildInvalidMessage,
    describeDuplicate: (id) => t("tactics_techniques_editor.duplicate", { id }),
    // The input doubles as a catalogue search, so text appearing in a row is a query, not a bad ID.
    validateInput: (candidate, raw) => matchesCatalogue(options, raw) ? null : validateId(candidate)
  });
  const isSearching = input.trim().length > 0;
  const renderOption = (option) => {
    if (option.name === void 0) return /* @__PURE__ */ React.createElement(OptionRow, { $depth: 0 }, option.label);
    const text = `${isSearching ? option.breadcrumb : option.name} (${option.value})`;
    return /* @__PURE__ */ React.createElement(OptionRow, { $depth: isSearching ? 0 : option.depth }, /* @__PURE__ */ React.createElement(OptionLabel, { title: text }, text));
  };
  const renderChip = ({ value: id }) => {
    const label = formatMitreCategory(id, nameById);
    return /* @__PURE__ */ React.createElement(ChipLabel, { title: label }, label);
  };
  const selectableOptions = options.map((option) => ({
    ...option,
    disabled: isAtMax || value.includes(option.value)
  }));
  const errorMessages = [
    error ? String(error) : null,
    ...messages,
    isAtMax ? t("tactics_techniques_editor.max_reached", { max: MAX_TACTICS_TECHNIQUES }) : null
  ].filter(Boolean);
  return /* @__PURE__ */ React.createElement(FormGroup, { validationState: errorMessages.length > 0 ? "error" : null }, /* @__PURE__ */ React.createElement(
    Select,
    {
      key: hasCatalogue ? "catalogue" : "bare",
      ...selectProps,
      ...reactSelectProps,
      ...{
        // A typed ID fuzzy-matches rows it isn't (T1685 matches T1608.005) and Enter takes the
        // first row, so the Create row has to lead. It only appears when the input is a valid
        // ID that isn't already added.
        createOptionPosition: "first",
        onKeyDown: (e) => {
          if (e.key !== "Enter" && e.key !== "Tab") return;
          const normalized = normalizeId(input);
          if (normalized && value.includes(normalized)) {
            setDuplicateAttempt(normalized);
            e.preventDefault();
            return;
          }
          flagDuplicateAttempt();
        }
      },
      inputId: "tactics-techniques",
      multi: true,
      allowCreate: true,
      options: selectableOptions,
      optionRenderer: renderOption,
      valueRenderer: renderChip,
      placeholder: t("tactics_techniques_editor.placeholder"),
      disabled,
      "aria-label": t("tactics_techniques.label")
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: hasCatalogue ? t("tactics_techniques_editor.help_with_catalogue") : t("tactics_techniques_editor.help"),
      error: errorMessages.length > 0 ? errorMessages.join(" ") : void 0
    }
  ));
};
const TacticsTechniquesEditorPlugin = ({ value, onChange, disabled = false, error = null }) => {
  const { t } = useTranslation("enterprise_security_app");
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  if (!isValidSecurityLicense) return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "tactics_techniques_editor.title", components: { small: /* @__PURE__ */ React.createElement("small", null) } })), /* @__PURE__ */ React.createElement("p", null, t("tactics_techniques_editor.description")), /* @__PURE__ */ React.createElement(Editor, { value, onChange, disabled, error }));
};
export default TacticsTechniquesEditorPlugin;
